import {Template} from "../../../../components/template/template.jsx";
import React, {useEffect, useRef, useState} from "react";
import Button from "../../../../components/button/button.jsx";
import {FaPlus} from "react-icons/fa";
import {useParams} from "react-router";
import {Card} from "../../../../components/card/card.jsx";
import {
  buildVehicleForm, buildVehicleJSON,
  fuelTypeArr,
  initialVehiclesForm,
  vehicleOwnershipArr,
  vehicleStatusArr,
  vehicleTypeArr
} from "../utils/vehicles-utils.js";
import SelectComponent from "../../../../components/select/select-component.jsx";
import {useGeneralInput, useGeneralInputSpecialCase} from "../../../../hooks/use-general-input.js";
import InputForm from "../../../../components/input/input-form.jsx";
import InputTextarea from "../../../../components/input/input-textarea.jsx";
import ReviewItemsList from "../components/review-items-list.jsx";
import vehiclesService from "../../../../services/web/vehicles-service.js";
import DamageTable from "../components/damage-table.jsx";
import {MaskDefault} from "../../../../utils/mask.js";
import VehiclesCameraTable from "../components/vehicles-camera-table.jsx";
import {FaArrowUpRightFromSquare} from "react-icons/fa6";
import VehiclesModalCameraTable from "../components/vehicles-modal-camera-table.jsx";
import projectUtils from "../../../../utils/project-utils.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import vmsService from "../../../../services/web/vms-service.js";
import SentryRow from "../../../../components/template/sentry-row.jsx";
import InputSwitch from "../../../../components/input/input-switch.jsx";
import EntityActionCard from "../../entity-action-card/entity-action-card.jsx";
import useVehiclesQueries from "../hooks/use-vehicles-queries.js";
import {useEntityMutation} from "../../../../hooks/use-entity-mutation.js";

const FORM_ALLOWED_STATUS = ["DECREASE", "AVAILABLE"];
const VehiclesForm = () => {
  const {id} = useParams();
  const [form, setForm] = useState(initialVehiclesForm);
  const [rawDamages, setRawDamages] = useState([]);
  const [ownersOptions, setOwnersOptions] = useState([]);
  const [reviewItems, setReviewItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const initializedVehicleId = useRef(null);
  const initializedReviewItems = useRef(false);
  const initializedDamages = useRef(false);
  const handleInput = useGeneralInput(setForm);
  const handleSpecialInput = useGeneralInputSpecialCase(setForm);
  const navigate = navigateHook();
  const {
    vehicleData,
    vehicleDamages,
    reviewItemsData,
    vehicleClasses,
    cameras,
    inspectionsItems,
    owners,
    isLoading,
  } = useVehiclesQueries(id);

  const mutation = useEntityMutation({
    service: vehiclesService,
    getInvalidateKeys: (data, variable) =>
      [
        ["vehicles", "detail", id],
        ["vehicles", "damages", id],
        ["vehicles", "review-items-form", id],
      ],
    navigatePath: "vehicles",
    onCustomSuccess: () => {
      setRawDamages(previousDamages => previousDamages.filter(damage => !damage.checked));
      setForm(previousForm => ({...previousForm, vehicle_damage: []}));
      initializedDamages.current = false;
    },
  })

  const isReadOnly = vehicleData?.status === 'GARNISHED' || vehicleData?.status === 'MAINTENANCE' || vehicleData?.status === 'USING';

  const handleCreateOwner = (newOwner) => {
    setOwnersOptions(prev => {
      handleSpecialInput("owner_id", newOwner);
      return [...prev, {value: newOwner, label: newOwner}]
    })
  };
  const save = () => {
    const payload = buildVehicleJSON(form, reviewItems);
    id ? mutation.mutate({method: "update", payload: payload, id: id}) : mutation.mutate({method: "create", payload});
  }

  useEffect(() => {
    initializedVehicleId.current = null;
    initializedReviewItems.current = false;
    initializedDamages.current = false;
  }, [id]);

  useEffect(() => {
    if(isLoading || initializedReviewItems.current) return;
    setReviewItems(reviewItemsData);
    initializedReviewItems.current = true;
  }, [isLoading, reviewItemsData]);

  useEffect(() => {
    if(isLoading || initializedDamages.current) return;
    setRawDamages(vehicleDamages);
    initializedDamages.current = true;
  }, [isLoading, vehicleDamages]);

  useEffect(() => {
    if(isLoading || !vehicleData || initializedVehicleId.current === id) return;
    setForm(buildVehicleForm(vehicleData, cameras));
    initializedVehicleId.current = id;
  }, [cameras, id, isLoading, vehicleData]);


  useEffect(() => {
    if(isLoading) return;
    setOwnersOptions(owners)
  }, [isLoading]);

  return(
    <Template isLoading={isLoading || mutation.isPending}>
      <Template.Header>
        <Template.Title>
          Veículo
        </Template.Title>
        { !!id &&
          <div className="flex gap-2">
            <div className="flex gap-2">
              { !!id && vehicleData?.status === 'AVAILABLE' &&
                <Button onClick={() => {
                  navigate(`/maintenances/form?cp=${id}`);
                }}><FaPlus/>Adicionar manutenção</Button>
              }
              <Button target="outline-primary" onClick={() => {
                setForm(initialVehiclesForm);
                navigate('/vehicles/form');
              }}><FaPlus/>Adicionar veículo</Button>
            </div>
          </div>
        }
      </Template.Header>
      <Template.Body>
        <div className="flex flex-col gap-2">
          <Card>
            <Card.Header>
              <Card.Title>Dados</Card.Title>
            </Card.Header>
            <Card.Body>
              <div className="flex flex-col gap-4">
                <SentryRow className="items-center">
                  <div className="col-span-2">
                    <SelectComponent
                      id="status"
                      label="Status *"
                      isDisabled={isReadOnly}
                      options={vehicleStatusArr.filter(status => FORM_ALLOWED_STATUS.some(allowedStatus => allowedStatus === status.value))}
                      value={vehicleStatusArr.find(status => status.value === form.status) ?? ""}
                      onChange={option => handleSpecialInput('status', option?.value) ?? null}
                    />
                  </div>
                  <div className="col-span-2">
                    <InputForm
                      id="prefix"
                      label="Prefixo *"
                      disabled={isReadOnly}
                      value={form.prefix}
                      onChange={handleInput}
                    />
                  </div>
                  <div className="col-span-2">
                    <InputForm
                      id="plate"
                      label="Placa"
                      disabled={isReadOnly}
                      value={form.plate}
                      onChange={handleInput}
                      inputClass="uppercase"
                      mask={MaskDefault.plate}
                    />
                  </div>
                  <div className="col-span-2">
                    <InputForm
                      id="renavam"
                      label="RENAVAM"
                      disabled={isReadOnly}
                      value={form.renavam}
                      type="number"
                      onChange={handleInput}
                    />
                  </div>
                  <div className="col-span-4">
                    <InputForm
                      id="chassis"
                      label="Chassi"
                      disabled={isReadOnly}
                      value={form.chassis}
                      onChange={handleInput}
                    />
                  </div>
                  <div className="col-span-2">
                    <InputForm
                      id="brand"
                      label="Marca"
                      disabled={isReadOnly}
                      value={form.brand}
                      onChange={handleInput}
                    />
                  </div>
                  <div className="col-span-2">
                    <InputForm
                      id="model"
                      label="Modelo"
                      disabled={isReadOnly}
                      value={form.model}
                      onChange={handleInput}
                    />
                  </div>
                  <div className="col-span-2">
                    <InputForm
                      id="sub_model"
                      label="Submodelo"
                      disabled={isReadOnly}
                      value={form.sub_model}
                      onChange={handleInput}
                    />
                  </div>
                  <div className="col-span-2">
                    <InputForm
                      id="color"
                      label="Cor"
                      disabled={isReadOnly}
                      value={form.color}
                      onChange={handleInput}
                    />
                  </div>
                  <div className="col-span-2">
                    <InputForm
                      id="year"
                      label="Ano de fabricação"
                      disabled={isReadOnly}
                      value={form.year}
                      onChange={handleInput}
                    />
                  </div>
                  <div className="col-span-2">
                    <InputForm
                      id="model_year"
                      label="Ano do modelo"
                      disabled={isReadOnly}
                      value={form.model_year}
                      onChange={handleInput}
                    />
                  </div>
                  <div className="col-span-2">
                    <SelectComponent
                      id="type"
                      label="Tipo *"
                      options={vehicleTypeArr}
                      isDisabled={isReadOnly}
                      value={vehicleTypeArr.find(status => status.value === form?.type) ?? "" }
                      onChange={(option) => handleSpecialInput("type", option?.value) ?? null}
                    />
                  </div>
                  <div className="col-span-2">
                    <InputForm
                      id="tank_max"
                      label="Capacidade do tanque"
                      disabled={isReadOnly}
                      value={form.tank_max}
                      type="number"
                      onChange={handleInput}
                    />
                  </div>
                  <div className="col-span-2">
                    <SelectComponent
                      id="fuel_type"
                      label="Tipo de combustível"
                      isDisabled={isReadOnly}
                      options={fuelTypeArr}
                      value={fuelTypeArr.find(status => status.value === form?.fuel_type) ?? "" }
                      onChange={(option) => handleSpecialInput("fuel_type", option?.value) ?? null}
                    />
                  </div>
                  <div className="col-span-2">
                    <InputForm
                      id="actual_km"
                      label="Km atual"
                      disabled={isReadOnly}
                      value={form.actual_km}
                      integerOnly
                      type="number"
                      onChange={handleInput}
                    />
                  </div>
                  <div className="col-span-4">
                    <InputSwitch
                      id="km_control"
                      label="Controle de km"
                      disabled={isReadOnly}
                      checked={form.km_control}
                      onChange={checked => handleSpecialInput("km_control", checked)}
                    />
                  </div>
                  <div className="col-span-2">
                    <SelectComponent
                      id="scheme"
                      label="Regime"
                      isDisabled={isReadOnly}
                      options={vehicleOwnershipArr}
                      value={vehicleOwnershipArr.find(ownership => ownership.value === form.scheme) ?? null}
                      onChange={option => handleSpecialInput("scheme", option?.value) ?? null}
                    />
                  </div>
                  <div className="col-span-2">
                    <SelectComponent
                      id="owner_id"
                      label="Proprietário"
                      isDisabled={isReadOnly}
                      options={ownersOptions}
                      value={ownersOptions.find(ownership => ownership.value === form.owner_id) ?? null}
                      onChange={option => handleSpecialInput("owner_id", option?.value) ?? null}
                      isCreatable
                      formatCreateLabel={(inputValue) => `Adicionar "${inputValue}"`}
                      onCreateOption={(newOwner) => handleCreateOwner(newOwner)}
                    />
                  </div>
                  <div className="col-span-2">
                    <SelectComponent
                      id="vehicle_classes_id"
                      label="Classe"
                      isDisabled={isReadOnly}
                      options={vehicleClasses}
                      value={vehicleClasses.find(classe => classe.value === form.vehicle_classes_id) ?? null}
                      onChange={option => handleSpecialInput("vehicle_classes_id", option?.value) ?? null}
                    />
                  </div>
                  <div className="col-span-2">
                    <SelectComponent
                      id="inspection_items_id"
                      label="Vistoria"
                      isDisabled={isReadOnly}
                      options={inspectionsItems}
                      value={inspectionsItems.find(item => item.value === form.inspection_items_id) ?? null}
                      onChange={option => handleSpecialInput("inspection_items_id", option?.value) ?? null}
                    />
                  </div>
                  <div className="col-span-4">
                    <InputSwitch
                      id="allow_failure_reporting"
                      label="Permitir relato de avaria e vistoria"
                      disabled={isReadOnly}
                      checked={form.allow_failure_reporting}
                      onChange={checked => handleSpecialInput("allow_failure_reporting", checked)}
                    />
                  </div>
                  <div className="col-span-12">
                    <InputTextarea
                      id="observation"
                      label="Observação"
                      disabled={isReadOnly}
                      textAreaClass="min-h-[120px]"
                      value={form.observation}
                      onChange={handleInput}
                    />
                  </div>
                </SentryRow>
              </div>
            </Card.Body>
          </Card>
          {!!id &&
            <Card>
              <Card.Header>
                <Card.Title>Avarias ativas</Card.Title>
              </Card.Header>
              <Card.Body>
                <DamageTable data={rawDamages} setData={setRawDamages} setForm={setForm} disabled={isReadOnly}/>
              </Card.Body>
            </Card>
          }
          <Card>
            <Card.Header>
              <Card.Title>Manutenção</Card.Title>
            </Card.Header>
            <Card.Body>
              { reviewItems.length > 0 &&
                <ReviewItemsList setItems={setReviewItems} items={reviewItems} currentKm={form.actual_km} disabled={isReadOnly} />
              }
            </Card.Body>
          </Card>
          <Card>
            <Card.Header>
              <Card.Title>Associação de câmeras de vídeo</Card.Title>
            </Card.Header>
            <Card.Body>
              <div className="flex flex-col gap-2">
                <div className="flex gap-2 items-center">
                  <Button onClick={() => setShowModal(true)} target="outline-primary" className="flex-none" disabled={isReadOnly}>Selecionar câmeras</Button>
                  { form.video_cameras.length >= 4 &&
                    <div className="flex items-center bg-[#fbf0da] text-[#73510d] text-[16px] py-1 px-3">Limite de 4 câmeras associadas atingido.</div>
                  }
                </div>
                <VehiclesCameraTable data={form.video_cameras} setData={setForm} disabled={isReadOnly}/>
                <a className="flex items-center gap-1 text-[#2a3947]! cursor-pointer hover:text-[#386bc0]!" onClick={() => {
                  navigate('/video-cameras', true);
                }}><FaArrowUpRightFromSquare/>Adicionar câmera</a>
              </div>
            </Card.Body>
          </Card>
          <EntityActionCard onSave={save} showDeleteButton={false} disableSaveButton={isReadOnly}/>
        </div>
        <VehiclesModalCameraTable showModal={showModal} setShowModal={setShowModal} data={form.video_cameras} setData={setForm} disabled={isReadOnly}/>
      </Template.Body>
    </Template>
  )
}

export default VehiclesForm;